'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronRightIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Card, Badge, Link, Numeric } from '@/components/ui';
import { apiGet } from '@/lib/api';
import { drawModel } from '@/models/drawModel';
import type { Database } from '@/types/supabase';
import type { DivisionsListGroup } from '@/app/[locale]/event/[eventId]/libs/clientProps';
import { evaluateRegistrationWindow } from '@/app/[locale]/event/[eventId]/[slug]/register/libs/registrationWindow';
import { isDivisionClosed } from '@/lib/divisionStatus';
import {
  divisionRow,
  publicDrawByGroup,
  sortGroups,
  type DivisionPill,
  type DivisionRowInput,
} from '@/app/[locale]/event/[eventId]/libs/divisionListPresentation';

type EventRow = Database['public']['Tables']['event']['Row'];
type PublicDraw = NonNullable<DivisionRowInput['draw']>;

interface DivisionsSummary {
  group_counts: Record<string, number>;
  active_group_counts?: Record<string, number>;
  full_group_ids?: string[];
  hidden?: boolean;
}

interface EventDivisionsListProps {
  groups: DivisionsListGroup[];
  /** The event row: its registration window is evaluated client-side after mount. */
  event: Pick<
    EventRow,
    'id' | 'status' | 'start_date' | 'end_date' | 'registration_opens' | 'registration_closes'
  >;
  locale: string;
  eventSlug: string;
  /** `hide_competition_from_public`: draws are never read nor linked. */
  competitionHidden: boolean;
}

const PILL_VARIANT: Record<DivisionPill, 'default' | 'secondary'> = {
  spotsLeft: 'default',
  full: 'secondary',
  closed: 'secondary',
  inProgress: 'default',
  finished: 'secondary',
};

/**
 * The public Divisions tab. Rows render at once from the server-fetched
 * groups; the entry counts (gated server-side by `hide_registrants_from_public`)
 * and the public draws fill in as they arrive. Every row's meaning — entry
 * door or draw door, which pill, which figures — is `divisionRow`'s verdict.
 */
export function EventDivisionsList({
  groups,
  event,
  locale,
  eventSlug,
  competitionHidden,
}: EventDivisionsListProps) {
  const eventId = event.id;
  const t = useTranslations('Event.divisions');
  const tDraws = useTranslations('Event.draws');
  const [summary, setSummary] = useState<DivisionsSummary | null>(null);
  const [drawByGroup, setDrawByGroup] = useState<Record<string, PublicDraw>>({});
  // Decided after mount so a cached (ISR) render can never pin a stale window:
  // server and first client render agree on "closed", then it settles.
  const [registrationOpen, setRegistrationOpen] = useState(false);
  useEffect(() => {
    setRegistrationOpen(evaluateRegistrationWindow(event).open);
  }, [event]);

  const baseHref = `/${locale}/event/${eventId}/${eventSlug}`;

  useEffect(() => {
    if (groups.length === 0) return;
    let cancelled = false;

    apiGet<DivisionsSummary>(`/api/event/${eventId}/divisions-summary`)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => console.error('Error fetching divisions summary:', err));

    if (!competitionHidden) {
      drawModel
        .getDrawListForEvent(eventId)
        .then((draws) => {
          if (!cancelled) setDrawByGroup(publicDrawByGroup(draws));
        })
        .catch((err) => console.error('Error fetching draws:', err));
    }

    return () => {
      cancelled = true;
    };
  }, [eventId, groups.length, competitionHidden]);

  if (groups.length === 0) {
    return (
      <Card className="flex min-w-0 flex-col items-center justify-center p-4 py-12 md:p-6 md:py-12 print:border-0 print:bg-transparent print:p-0 print:shadow-none">
        <UserGroupIcon className="text-muted mb-4 h-12 w-12" />
        <p className="text-muted text-center">{t('empty')}</p>
      </Card>
    );
  }

  const countsVisible = summary !== null && !summary.hidden;
  const fullIds = new Set(summary?.full_group_ids ?? []);

  const rows = sortGroups(groups).map((group) => ({
    group,
    row: divisionRow({
      group,
      draw: drawByGroup[group.id] ?? null,
      entryCount: countsVisible ? (summary.group_counts[group.id] ?? 0) : undefined,
      activeCount: countsVisible ? summary.active_group_counts?.[group.id] : undefined,
      full: fullIds.has(group.id),
      closed: isDivisionClosed(group),
      registrationOpen,
      competitionHidden,
    }),
  }));

  const drawsReleased = rows.filter(({ row }) => row.destination === 'draw').length;
  const totalEntries = countsVisible
    ? rows.reduce((sum, { row }) => sum + (row.entries ?? 0), 0)
    : null;

  const pillLabel = (pill: DivisionPill, spotsLeft: number | null) =>
    pill === 'spotsLeft' ? t('spotsLeft', { count: spotsLeft ?? 0 }) : t(pill);

  return (
    <Card className="min-w-0 p-4 md:p-6 print:border-0 print:bg-transparent print:p-0 print:shadow-none">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-foreground text-lg font-semibold">
          {t('count', { count: groups.length })}
          {/* Said only when it adds something: every division having a draw is the normal case. */}
          {drawsReleased > 0 && drawsReleased < groups.length && (
            <span className="text-muted font-normal">
              {' · '}
              {t('drawsReleased', { count: drawsReleased })}
            </span>
          )}
        </h2>
        {totalEntries !== null && (
          <span className="text-muted text-sm">{t('entries', { count: totalEntries })}</span>
        )}
      </div>

      <ul className="divide-border flex flex-col divide-y" data-testid="division-list">
        {rows.map(({ group, row }) => {
          const href =
            row.destination === 'draw'
              ? `${baseHref}/draw/${row.drawId}`
              : `${baseHref}/divisions/${group.id}`;
          const entriesKey = group.is_team ? 'teams' : 'players';
          return (
            <li key={group.id}>
              <Link
                href={href}
                className="hover:bg-surface ease-default grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-3.5 transition-colors duration-200"
              >
                <div className="min-w-0">
                  <div className="text-foreground flex flex-wrap items-center gap-2 font-semibold">
                    <span className="break-words">{group.name}</span>
                    {row.pill && (
                      <Badge variant={PILL_VARIANT[row.pill]}>
                        {pillLabel(row.pill, row.spotsLeft)}
                      </Badge>
                    )}
                  </div>
                  {(row.format || row.entries !== null) && (
                    <div className="text-muted mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      {row.format && <span>{tDraws(`format.${row.format}`)}</span>}
                      {row.entries !== null && (
                        <span>
                          <Numeric>{row.entries}</Numeric>{' '}
                          {tDraws(`entries.${entriesKey}`, { count: row.entries })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <ChevronRightIcon className="text-muted h-5 w-5 shrink-0" aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
